import { useState, useCallback, useEffect } from 'react';
import { useUserSetters } from '../context/UserContext/UserProvider';
import userAxios from '../api/axiosInstance';
import { validateAddressComponents } from 'lib';

const WIZARD_AUTO_OPENED_SESSION_KEY = 'inyc_event_wizard_auto_opened_session';

// Wizard step definitions - ordered to match posting page flow
export const WIZARD_STEPS = [
  {
    id: 'title',
    field: 'name',
    label: 'add event title',
    required: true,
  },
  {
    id: 'category',
    field: 'category',
    label: 'select category',
    required: true,
  },
  {
    id: 'tags',
    field: 'tags',
    label: 'add genres / subgenres',
    required: false,
  },
  {
    id: 'datetime',
    field: 'date',
    label: 'set date & time',
    required: true,
  },
  {
    id: 'recurrence',
    field: 'recurrence',
    label: 'event frequency',
    required: false,
  },
  {
    id: 'location',
    field: 'location',
    label: 'add location',
    required: true,
  },
  {
    id: 'images',
    field: 'images',
    label: 'add images',
    required: false,
  },
  {
    id: 'socials',
    field: 'socials',
    label: 'add social links',
    required: false,
  },
  {
    id: 'description',
    field: 'description',
    label: 'add description',
    required: false,
  },
  {
    id: 'tickets',
    field: 'tickets',
    label: 'add tickets',
    required: false,
  },
  {
    id: 'linked_artists',
    field: 'linked_artists',
    label: 'tag artists',
    required: false,
  },
  {
    id: 'age_restriction',
    field: 'age_restriction',
    label: 'minimum age',
    required: false,
  },
  {
    id: 'review',
    field: null,
    label: 'review',
    required: false,
  },
];

/**
 * Custom hook to manage the Event Posting Wizard state
 * Uses user.settings.showEventWizard from the backend for persistence
 */
export const useEventWizard = () => {
  const { user, setUser } = useUserSetters();
  
  // Check if wizard should show based on user settings
  // Default to true (show wizard) for new users or if setting is undefined
  const showEventWizard = user?.settings?.showEventWizard !== false;

  // Wizard visibility
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  
  // Current step index
  const [currentStep, setCurrentStep] = useState(0);
  
  // Get default date and times (today's date, next hour rounded, +2 hours for end)
  const getDefaultDateAndTimes = () => {
    const now = new Date();
    // Default date is today in YYYY-MM-DD format (local time)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const startDate = `${year}-${month}-${day}`;
    
    // Default start time is next hour
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const startTime = now.toTimeString().slice(0, 5);
    now.setHours(now.getHours() + 2);
    const endTime = now.toTimeString().slice(0, 5);
    return { startDate, startTime, endTime };
  };
  
  const defaults = getDefaultDateAndTimes();
  
  // Collected wizard data
  const [wizardData, setWizardData] = useState({
    name: '',
    category: 'music',
    tags: [],
    date: {
      startDate: defaults.startDate,
      startTime: defaults.startTime,
      endTime: defaults.endTime,
    },
    // Recurrence settings
    recurrence: {
      type: 'single', // 'single', 'repeating', 'multiple'
      // For repeating events
      repeatEveryNumber: 1,
      repeatEveryPeriod: 'week', // 'day', 'week', 'month'
      repeatOn: [], // for weekly: ['mon', 'wed', 'fri'] etc.
      monthlyType: 'dayOfMonth', // 'dayOfMonth' or 'dayOfWeek' (e.g., "2nd Monday")
      repeatEndsOn: null, // date string
      // For multiple dates
      extraDates: [], // array of { startDate, startTime, endTime }
    },
    location: {
      address: '',
      coordinates: null,
      venue: null,
    },
    images: [],
    socials: [], // array of { platform, link }
    description: '',
    ticketedSelected: false,
    ticketed: false,
    tickets: [],
    linked_artists: [],
    age_restriction: '',
  });

  // Animation direction for transitions
  const [direction, setDirection] = useState('forward');

  // Auto-show wizard for first-time users (when showEventWizard is true)
  useEffect(() => {
    if (!user || !showEventWizard) return;

    let hasAutoOpened = false;
    try {
      hasAutoOpened = sessionStorage.getItem(WIZARD_AUTO_OPENED_SESSION_KEY) === 'true';
    } catch (error) {
      hasAutoOpened = false;
    }

    if (hasAutoOpened) return;

    // Small delay to let the page render first
    const timer = setTimeout(() => {
      setIsWizardOpen(true);
      try {
        sessionStorage.setItem(WIZARD_AUTO_OPENED_SESSION_KEY, 'true');
      } catch (error) {
        // Ignore storage failures; the wizard still behaves normally.
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [user, showEventWizard]);

  // Update user setting on the backend
  const updateWizardSetting = useCallback(async (value) => {
    // Optimistically update local state
    setUser((prevUser) => ({
      ...prevUser,
      settings: {
        ...prevUser?.settings,
        showEventWizard: value,
      },
    }));

    try {
      await userAxios.get(`/user/me/update-event-wizard-setting?value=${value}`);
    } catch (error) {
      console.warn('Failed to update wizard setting:', error);
      // Revert on error
      setUser((prevUser) => ({
        ...prevUser,
        settings: {
          ...prevUser?.settings,
          showEventWizard: !value,
        },
      }));
    }
  }, [setUser]);

  // Open wizard manually
  const openWizard = useCallback(() => {
    setCurrentStep(0);
    setDirection('forward');
    setIsWizardOpen(true);
  }, []);

  // Close wizard (doesn't change the setting - user can still see it next time)
  const closeWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  // Skip wizard (just closes it, setting is only changed when "don't show again" is checked)
  const skipWizard = useCallback(() => {
    setIsWizardOpen(false);
  }, []);

  // Disable wizard permanently (called when "don't show again" is checked)
  const disableWizard = useCallback(() => {
    updateWizardSetting(false);
  }, [updateWizardSetting]);

  // Enable wizard (can be used from settings)
  const enableWizard = useCallback(() => {
    updateWizardSetting(true);
  }, [updateWizardSetting]);

  // Go to next step
  const nextStep = useCallback(() => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setDirection('forward');
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setDirection('backward');
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Go to specific step
  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < WIZARD_STEPS.length) {
      setDirection(stepIndex > currentStep ? 'forward' : 'backward');
      setCurrentStep(stepIndex);
    }
  }, [currentStep]);

  // Update wizard data for a specific field
  const updateWizardData = useCallback((field, value) => {
    setWizardData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Complete wizard and return collected data
  const completeWizard = useCallback(() => {
    closeWizard();
    return wizardData;
  }, [closeWizard, wizardData]);

  // Reset wizard data
  const resetWizardData = useCallback(() => {
    const defs = getDefaultDateAndTimes();
    setWizardData({
      name: '',
      category: 'music',
      tags: [],
      date: {
        startDate: defs.startDate,
        startTime: defs.startTime,
        endTime: defs.endTime,
      },
      recurrence: {
        type: 'single',
        repeatEveryNumber: 1,
        repeatEveryPeriod: 'week',
        repeatOn: [],
        monthlyType: 'dayOfMonth',
        repeatEndsOn: null,
        extraDates: [],
      },
      location: {
        address: '',
        coordinates: null,
        venue: null,
      },
      images: [],
      socials: [],
      description: '',
      ticketedSelected: false,
      ticketed: false,
      tickets: [],
      linked_artists: [],
      age_restriction: '',
    });
    setCurrentStep(0);
  }, []);

  // Check if current step data is valid
  const isCurrentStepValid = useCallback(() => {
    const step = WIZARD_STEPS[currentStep];
    if (!step.required) {
      // Special case: recurrence step is conditionally required
      if (step.id === 'recurrence') {
        const recurrence = wizardData.recurrence || {};
        const recurrenceType = recurrence.type || 'single';
        
        // If it's a repeating event, require a valid end date
        if (recurrenceType === 'repeating') {
          if (!recurrence.repeatEndsOn) return false;
          // Validate that end date is after start date
          if (wizardData.date?.startDate) {
            const startDate = new Date(wizardData.date.startDate);
            const endDate = new Date(recurrence.repeatEndsOn);
            // End date must be at least 1 day after start date
            endDate.setHours(0, 0, 0, 0);
            startDate.setHours(0, 0, 0, 0);
            return endDate > startDate;
          }
          return false;
        }
        // If it's multiple dates, require at least one extra date
        if (recurrenceType === 'multiple') {
          return recurrence.extraDates && recurrence.extraDates.length > 0;
        }
        // Single event is always valid
        return true;
      }
      return true;
    }

    switch (step.id) {
      case 'title':
        return wizardData.name.trim().length > 0;
      case 'category':
        return wizardData.category.length > 0;
      case 'datetime':
        return wizardData.date.startDate && wizardData.date.startTime && wizardData.date.endTime;
      case 'location':
        // Valid if we have a verified venue OR valid address fields with coordinates
        const hasVenue = wizardData.location.venue && wizardData.location.venue._id;
        if (hasVenue) {
          return true; // Verified venues are always valid
        }
        
        // Check if we have coordinates
        const hasCoordinates = wizardData.location.coordinates && 
          wizardData.location.coordinates.lat && 
          wizardData.location.coordinates.lng;
        
        if (!hasCoordinates) {
          return false; // Must have location selected on map
        }
        
        // Validate address fields if present
        if (wizardData.location.addressFields) {
          const { isValid } = validateAddressComponents(wizardData.location.addressFields);
          return isValid;
        }
        
        // If no addressFields but has coordinates and address string, consider it valid
        // (this handles edge cases where addressFields might not be set)
        const hasAddress = wizardData.location.address && wizardData.location.address.trim().length > 0;
        return hasAddress;
      default:
        return true;
    }
  }, [currentStep, wizardData]);

  // Get current step info
  const currentStepInfo = WIZARD_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const isReviewStep = currentStepInfo?.id === 'review';
  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  return {
    // State
    showEventWizard, // Whether wizard is enabled in settings
    isWizardOpen,
    currentStep,
    wizardData,
    direction,
    
    // Computed
    currentStepInfo,
    isFirstStep,
    isLastStep,
    isReviewStep,
    progress,
    totalSteps: WIZARD_STEPS.length,
    steps: WIZARD_STEPS,
    
    // Actions
    openWizard,
    closeWizard,
    skipWizard,
    disableWizard,
    enableWizard,
    nextStep,
    prevStep,
    goToStep,
    updateWizardData,
    completeWizard,
    resetWizardData,
    isCurrentStepValid,
  };
};

export default useEventWizard;
